import React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Camera, MapPin, Users } from "lucide-react";
import {
  fetchFeed,
  fetchRecentCheckins,
  type FeedTab,
  type PostKind,
  type RecentCheckin,
} from "../../api/social";
import { useAuth } from "../../hooks/useAuth";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Button } from "../../components/Button";
import { PostCard } from "../../components/PostCard";
import { Composer, UsernameClaimPrompt } from "../../components/PostComposer";
import { ProfileIdentity } from "../../components/ProfileIdentity";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { getBannerAsset } from "../../lib/cosmetics";

// ─── Feed tabs ──────────────────────────────────────────────────────────────────
// The post feed's two server tabs plus a client-only third: recent check-ins
// (a different endpoint entirely — never sent as ?tab to /social/feed).

type CommunityTab = FeedTab | "checkins";

const FEED_TABS: Array<{ tab: CommunityTab; label: string }> = [
  { tab: "following", label: "Following" },
  { tab: "popular", label: "Popular" },
  { tab: "checkins", label: "Recent check-ins" },
];

function FeedTabs({ active, onSelect }: { active: CommunityTab | null; onSelect: (tab: CommunityTab) => void }) {
  return (
    <nav style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border-subtle)" }}>
      {FEED_TABS.map(({ tab, label }) => {
        const isActive = active === tab;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => onSelect(tab)}
            style={{
              padding: "8px 14px 10px",
              fontSize: 13,
              fontWeight: isActive ? 600 : 500,
              fontFamily: "var(--font-sans)",
              color: isActive ? "var(--text-primary)" : "var(--text-muted)",
              background: "none",
              border: "none",
              cursor: "pointer",
              borderBottom: `2px solid ${isActive ? "var(--accent)" : "transparent"}`,
              marginBottom: -1,
              transition: "color 0.15s ease, border-color 0.15s ease",
            }}
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

// ─── Recent check-ins grid ──────────────────────────────────────────────────────

function CheckinCard({ checkin }: { checkin: RecentCheckin }) {
  const authorName = checkin.author.name ?? checkin.author.username ?? "Explorer";
  const bannerSrc = getBannerAsset(checkin.author.banner?.metadata.assetKey);
  const linkable = checkin.author.profilePublic && checkin.author.username;
  // Summit check-ins (spec 34) carry the peak in the landmark slot.
  const placeRoute = checkin.landmark.itemType === "peak" ? "/peaks/$slug" : "/landmarks/$slug";

  const identity = (
    <ProfileIdentity name={authorName} avatarUrl={checkin.author.avatarUrl} size="sm" />
  );

  return (
    <AtlasPanel style={{ overflow: "hidden", display: "flex", flexDirection: "column" }} bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", flex: 1 }}>
      {/* Header — equipped banner strip behind the identity row (PostCard pattern) */}
      <div style={{ position: "relative", padding: "10px 12px", overflow: "hidden" }}>
        {bannerSrc && (
          <>
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url("${bannerSrc}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                background: "color-mix(in srgb, var(--surface-card) 62%, transparent)",
              }}
            />
          </>
        )}
        {linkable ? (
          <Link
            to="/explorers/$username"
            params={{ username: checkin.author.username! }}
            style={{ position: "relative", display: "block", textDecoration: "none", color: "inherit", minWidth: 0 }}
          >
            {identity}
          </Link>
        ) : (
          <div style={{ position: "relative", minWidth: 0 }}>{identity}</div>
        )}
      </div>

      {/* Photo links through to the landmark (or summit) it was taken at */}
      <Link
        to={placeRoute}
        params={{ slug: checkin.landmark.slug }}
        style={{ display: "block", lineHeight: 0 }}
        aria-label={`View ${checkin.landmark.name}`}
      >
        <img
          src={checkin.photoUrl}
          alt={checkin.caption ?? `Check-in at ${checkin.landmark.name}`}
          loading="lazy"
          style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", display: "block" }}
        />
      </Link>

      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        {checkin.caption && (
          <p
            style={{
              fontSize: 13,
              color: "var(--text-primary)",
              lineHeight: 1.45,
              margin: 0,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {checkin.caption}
          </p>
        )}
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          <Link
            to={placeRoute}
            params={{ slug: checkin.landmark.slug }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              textDecoration: "none",
              color: "var(--text-secondary)",
              fontSize: 12.5,
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            <MapPin size={13} style={{ flexShrink: 0, color: "var(--sky-400)" }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {checkin.landmark.name}
              {checkin.landmark.region ? ` · ${checkin.landmark.region}` : ""}
            </span>
          </Link>
          <span className="tnum" style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>
            {new Date(checkin.checkedInAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
          </span>
        </div>
      </div>
    </AtlasPanel>
  );
}

function CheckinsGrid() {
  const checkinsQuery = useInfiniteQuery({
    queryKey: ["social/checkins"],
    queryFn: ({ pageParam }) => fetchRecentCheckins(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const checkins = checkinsQuery.data?.pages.flatMap((p) => p.items) ?? [];

  if (checkinsQuery.isLoading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 16 }}>
        <Skel height="280px" radius="var(--radius-lg)" />
        <Skel height="280px" radius="var(--radius-lg)" />
        <Skel height="280px" radius="var(--radius-lg)" />
      </div>
    );
  }

  if (checkins.length === 0) {
    return (
      <EmptyState
        icon={<Camera size={20} />}
        title="No check-ins yet"
        hint="Photo check-ins from landmarks across the community will appear here — visit a landmark page to add yours."
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 16 }}>
        {checkins.map((checkin) => (
          <CheckinCard key={checkin.id} checkin={checkin} />
        ))}
      </div>
      {checkinsQuery.hasNextPage && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="secondary"
            onClick={() => void checkinsQuery.fetchNextPage()}
            disabled={checkinsQuery.isFetchingNextPage}
          >
            {checkinsQuery.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── FeedPage ───────────────────────────────────────────────────────────────────

export default function FeedPage() {
  const { user } = useAuth();
  const search = useSearch({ from: "/_protected/feed" });

  const attach = React.useMemo(() => {
    if (!search.attach) return null;
    const [rawKind, id] = search.attach.split(":");
    if (!id) return null;
    if (rawKind === "achievement" || rawKind === "activity") {
      return { kind: rawKind as PostKind, id };
    }
    return null;
  }, [search.attach]);

  // null = let the server pick the default tab (following when the user
  // follows anyone, else popular) — the response echoes what it resolved.
  // "checkins" is client-only and never reaches /social/feed.
  const [selectedTab, setSelectedTab] = React.useState<CommunityTab | null>(null);
  const feedTab = selectedTab === "checkins" ? null : selectedTab;

  const feedQuery = useInfiniteQuery({
    queryKey: ["social/feed", feedTab ?? "default"],
    queryFn: ({ pageParam }) => fetchFeed(pageParam as string | undefined, feedTab ?? undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: selectedTab !== "checkins",
  });

  const firstPage = feedQuery.data?.pages[0];
  const activeTab = selectedTab ?? firstPage?.tab ?? null;
  const posts = feedQuery.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="r-page" style={{ padding: 28, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ maxWidth: 660, width: "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Community</p>
          <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>Feed</h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 6, marginBottom: 0 }}>
            Share your adventures, cheer on other explorers, and give awards.
          </p>
        </div>

        {user && !user.username ? <UsernameClaimPrompt /> : <Composer attach={attach} />}

        <FeedTabs active={activeTab} onSelect={setSelectedTab} />

      {activeTab === "checkins" ? (
        <CheckinsGrid />
      ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {feedQuery.isLoading ? (
          <>
            <Skel height="160px" radius="var(--radius-lg)" />
            <Skel height="160px" radius="var(--radius-lg)" />
            <Skel height="160px" radius="var(--radius-lg)" />
          </>
        ) : posts.length === 0 ? (
          activeTab === "following" ? (
            <EmptyState
              icon={<Users size={20} />}
              title="Your following feed is quiet"
              hint="Follow explorers from their profiles to see their posts here — the Popular tab is a good place to find them."
            />
          ) : (
            <EmptyState
              icon={<Users size={20} />}
              title="No posts yet"
              hint="No posts yet — be the first to share an adventure."
            />
          )
        ) : (
          posts.map((post) => <PostCard key={post.id} post={post} />)
        )}

        {feedQuery.hasNextPage && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="secondary"
              onClick={() => void feedQuery.fetchNextPage()}
              disabled={feedQuery.isFetchingNextPage}
            >
              {feedQuery.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </div>
      )}
      </div>
    </div>
  );
}
